import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';

import { Sport } from '#src/__generated__/globalTypes.ts';
import { SHARING_ORIGIN } from '#src/config.ts';
import { FRONTEND_ASSET_HOST } from '#src/constants/assets.ts';
import { useIsMlbPage } from '#src/hooks/useIsMlbPage.ts';
import { useIsNBAPage } from '#src/hooks/useIsNBAPage.ts';
import { useSharedAcrossSportsPage as useSharedAccrossSportsPage } from '#src/hooks/useSharedAccrossSportsPage.ts';
import { getPrefix } from '#src/lib/i18n.ts';
import { metadatas as baseballMetadatas } from '#src/lib/seo/baseball.ts';
import { metadatas as commonMetadatas } from '#src/lib/seo/common.ts';
import { metadatas as footballMetadatas } from '#src/lib/seo/football.ts';
import { getLocalizedUrl } from '#src/lib/seo/localizedUrls.ts';
import { metadatas as nbaMetadatas } from '#src/lib/seo/nba.ts';
import type { ImageVariations } from '#src/lib/share.ts';

import { HrefLang } from './HrefLang.tsx';
import type { Options, RouteMetadata } from './index.tsx';
import { SeoContext } from './index.tsx';

type Props = {
  children: ReactNode;
};

const sportTitles = {
  [Sport.BASEBALL]: baseballMetadatas.default.title,
  [Sport.NBA]: nbaMetadatas.default.title,
  [Sport.FOOTBALL]: footballMetadatas.default.title,
};

const sportDescriptions = {
  [Sport.BASEBALL]: baseballMetadatas.default.description,
  [Sport.NBA]: nbaMetadatas.default.description,
  [Sport.FOOTBALL]: footballMetadatas.default.description,
};

const defaultImage = `${FRONTEND_ASSET_HOST}/meta/social-picture-2022.png`;
const defaultTwitter = '@sorare';

export const SeoProvider = ({ children }: Props) => {
  const { formatMessage, locale } = useIntl();
  const { pathname } = useLocation();
  const [title, setTitle] = useState<string | undefined>(undefined);
  const [description, setDescription] = useState<string | undefined>(undefined);
  const [img, setImg] = useState<
    string | Record<ImageVariations, string | null> | undefined | null
  >(undefined);
  const [twitter, setTwitter] = useState<string | undefined>(undefined);
  const [noIndexCount, setNoIndexCount] = useState(0);
  const [languageRestrictions, setLanguageRestrictions] = useState<
    readonly (readonly string[])[]
  >([]);
  const [routeMetadata, setRouteMetadataState] = useState<RouteMetadata>({});
  const sharedAcrossSportsPage = useSharedAccrossSportsPage();

  const registerNoIndex = useCallback(() => {
    setNoIndexCount(count => count + 1);
    return () => setNoIndexCount(count => count - 1);
  }, []);

  const registerAlternateLanguages = useCallback(
    (languages: readonly string[]) => {
      setLanguageRestrictions(restrictions => [...restrictions, languages]);
      return () =>
        setLanguageRestrictions(restrictions =>
          // Drop one entry only: two pages may share the same array.
          restrictions.filter(
            (_, index) => index !== restrictions.indexOf(languages)
          )
        );
    },
    []
  );

  const setPageMetadata = useCallback(
    (componentTitle: string, options?: Options) => {
      const titleWithNamespace =
        componentTitle.indexOf('•') >= 0 ||
        componentTitle.indexOf('- Sorare') >= 0 ||
        componentTitle.indexOf('– Sorare') >= 0 ||
        componentTitle.indexOf('Sorare') === 0
          ? componentTitle
          : `${componentTitle} • Sorare`;

      const seoTitle = options?.replaceFullTitle
        ? componentTitle
        : titleWithNamespace;

      setTitle(seoTitle);
      setDescription(options?.description);
      setImg(options?.img);
      setTwitter(options?.twitter);
      const unregisterNoIndex = options?.noindex ? registerNoIndex() : null;
      return () => {
        setTitle(undefined);
        setImg(undefined);
        setDescription(undefined);
        setTwitter(undefined);
        unregisterNoIndex?.();
      };
    },
    [registerNoIndex]
  );

  const setRouteMetadata = useCallback((metadata: RouteMetadata) => {
    setRouteMetadataState(metadata);
    return () => {
      setRouteMetadataState({});
    };
  }, []);

  const isMlbPage = useIsMlbPage();
  const isNBAPage = useIsNBAPage();

  let currentSport: Sport = Sport.FOOTBALL;
  if (isMlbPage) currentSport = Sport.BASEBALL;
  if (isNBAPage) currentSport = Sport.NBA;

  const defaultTitle = commonMetadatas.default.title;
  const defaultSportTitle = sportTitles[currentSport];
  const pageTitle =
    title ||
    routeMetadata.title ||
    (sharedAcrossSportsPage
      ? formatMessage(defaultTitle)
      : formatMessage(defaultSportTitle));

  const defaultDescription = commonMetadatas.default.description;
  const defaultSportDescription = sportDescriptions[currentSport];
  const pageDescription =
    description ||
    routeMetadata.description ||
    (sharedAcrossSportsPage
      ? formatMessage(defaultDescription)
      : formatMessage(defaultSportDescription));

  const pageImg = img || routeMetadata.img;
  const ogImage =
    pageImg && typeof pageImg === 'object'
      ? pageImg.post || pageImg.square || pageImg.story
      : pageImg;

  const value = useMemo(
    () => ({
      setPageMetadata,
      registerNoIndex,
      registerAlternateLanguages,
      setRouteMetadata,
    }),
    [
      setPageMetadata,
      registerNoIndex,
      registerAlternateLanguages,
      setRouteMetadata,
    ]
  );
  const noIndex = noIndexCount > 0;
  const alternateLanguages = useMemo(
    () =>
      languageRestrictions.length
        ? languageRestrictions.reduce((allowed, languages) =>
            allowed.filter(language => languages.includes(language))
          )
        : undefined,
    [languageRestrictions]
  );

  return (
    <SeoContext value={value}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="twitter:site" content={twitter || defaultTwitter} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta
          property="og:url"
          content={getLocalizedUrl(SHARING_ORIGIN, getPrefix(locale), pathname)}
        />
        <meta property="og:image" content={ogImage || defaultImage} />
        {noIndex ? <meta name="robots" content="noindex" /> : null}
      </Helmet>
      {noIndex ? null : <HrefLang languages={alternateLanguages} />}
      {children}
    </SeoContext>
  );
};
